import React from 'react';
import GridItem from '../../../../layout/GridComponents/GridItem';
import CustomTable from '../../../../tables/CustomTable';
import { CustomColumn } from '../../../../../types/components/tables/tableTypes';
import { Status } from '../../../../../types/redux/data/dataTypes';
import { formatESGWithUnit } from '../../../../../utilities/numberFormatters';
import makeStyles from '@mui/styles/makeStyles';
import { EsAlignmentTableData } from '../EsAlignmentMonitor.component';
import { mainColors } from '../../../../../styling/theme';
import RaptorStatusBox from '../../../../feedback/RaptorStatusBox.component';
import SecondLevelTable from './SecondLevelTable.component';
import { Theme } from '@mui/material';
import { EsAlignmentApiResponse } from '../services/queries';

interface EsAlignmentTableProps {
  data: EsAlignmentApiResponse;
}

const useStyles = makeStyles((theme: Theme) => ({
  tabsContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: '1.25rem',
    width: '100%',
  },
  tab_active: {
    all: 'unset',
    width: 'fit-content',
    height: '1.88rem',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '0 1.25rem',
    borderRadius: '0.25rem',
    fontWeight: 400,
    fontSize: '1.12rem',
    backgroundColor: mainColors.mainBlue,
    color: 'white',
    cursor: 'pointer',
    userSelect: 'none',
  },
  tab_inactive: {
    all: 'unset',
    width: 'fit-content',
    height: '1.88rem',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '0 1.25rem',
    borderRadius: '0.25rem',
    fontWeight: 400,
    fontSize: '1.12rem',
    backgroundColor: 'white',
    color: mainColors.mainBlue,
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover': {
      backgroundColor: mainColors.hoverOverWhite,
    },
  },
}));

// The standard columns for the table (Full Set)
const standardColumns: CustomColumn<EsAlignmentTableData>[] = [
  {
    title: 'Indicator',
    field: 'indicator',
    cellStyle: {
      textAlign: 'center',
    },
    headerStyle: {
      textAlign: 'center',
    },
  },
  {
    title: 'Indicator Name',
    field: 'indicator_name',
    cellStyle: {
      textAlign: 'center',
    },
    headerStyle: {
      textAlign: 'center',
    },
  },
  {
    title: 'Description',
    field: 'description',
    cellStyle: {
      textAlign: 'center',
    },
    headerStyle: {
      textAlign: 'center',
    },
  },
  {
    title: 'Value',
    field: 'absolute_value',
    cellStyle: {
      textAlign: 'center',
      whiteSpace: 'nowrap',
    },
    headerStyle: {
      textAlign: 'center',
    },
    render: (rowData) =>
      rowData.absolute_value != null
        ? Number(rowData.absolute_value).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        : 'N/A',
  },
  {
    title: 'Value%',
    field: 'value',
    cellStyle: {
      textAlign: 'center',
      whiteSpace: 'nowrap',
    },
    headerStyle: {
      textAlign: 'center',
    },
    render: (rowData) => formatESGWithUnit(rowData.value, rowData.unit),
  },
  {
    title: 'Limit Type',
    field: 'limit_type',
    cellStyle: {
      textAlign: 'center',
    },
    headerStyle: {
      textAlign: 'center',
    },
  },
  {
    title: 'Limit',
    field: 'limit',
    cellStyle: {
      textAlign: 'center',
      whiteSpace: 'nowrap',
    },
    headerStyle: {
      textAlign: 'center',
    },
    render: (rowData) => formatESGWithUnit(rowData.limit, rowData.unit),
  },
  {
    title: 'Pass / Fail',
    field: 'pass_fail',
    cellStyle: {
      textAlign: 'center',
    },
    headerStyle: {
      textAlign: 'center',
    },
    render: (rowData) => (
      <RaptorStatusBox status={rowData.pass_fail as Status} />
    ),
    width: '10%',
  },
];

// Build the columns using the columns in the configs
const buildColumns = (
  data: EsAlignmentApiResponse,
  columns: CustomColumn<EsAlignmentTableData>[],
): CustomColumn<EsAlignmentTableData>[] => {
  if (!data.configs || !data.configs.indicator_columns) {
    return columns;
  } else {
    const returnColumns: CustomColumn<EsAlignmentTableData>[] = [];
    data.configs.indicator_columns.forEach((column: string) => {
      columns.forEach((col: CustomColumn<EsAlignmentTableData>) => {
        if (col.field === column) {
          returnColumns.push(col);
        }
      });
    });
    return returnColumns;
  }
};

// Build the data using the indicators in the active tab
const buildTableData = (
  data: EsAlignmentApiResponse,
  tab: string,
): EsAlignmentTableData[] => {
  if (
    !data.data ||
    !data.data.length ||
    !data.configs.indicators ||
    !data.configs.indicators[tab]
  ) {
    return [];
  } else {
    return data.data;
  }
};

const EsAlignmentTable: React.FC<EsAlignmentTableProps> = ({ data }) => {
  const classes = useStyles();

  // from the configs item in the data object, get the tabs
  const [tabs] = React.useState<string[]>(Object.keys(data.configs.indicators));
  const [activeTab, setActiveTab] = React.useState<string>(
    Object.keys(data.configs.indicators)[0],
  );

  const columns = buildColumns(data, standardColumns);
  const tableData = buildTableData(data, activeTab);
  return (
    <GridItem xs={12} card>
      <CustomTable<EsAlignmentTableData>
        // The columns are built using only the columns in the configs
        columns={columns}
        showToolbar
        // The data is built using only the indicators in the active tab
        data={tableData}
        // If there is more than one tab, then the title is a row of interactive tabs, else it is just the active tab as a string
        title={
          tabs.length > 1 ? (
            <div className={classes.tabsContainer}>
              {Object.keys(data.configs.indicators).map((tab: string) => {
                return (
                  <div
                    className={
                      activeTab === tab
                        ? classes.tab_active
                        : classes.tab_inactive
                    }
                    onClick={() => setActiveTab(tab)}
                  >
                    {tab}
                  </div>
                );
              })}
            </div>
          ) : (
            activeTab
          )
        }
        options={{
          draggable: false,
          sorting: false,
          paging: false,
          search: true,
          exportButton: true,
        }}
        detailPanel={[
          {
            disabled: false,
            render: (rowData: EsAlignmentTableData) => {
              return (
                <SecondLevelTable data={data} rowData={rowData.underlying} />
              );
            },
          },
        ]}
      />
    </GridItem>
  );
};

export default EsAlignmentTable;
